import Element from "state/ui/editor/elements/Element";
import { getActiveSegments } from "lib/captionUtils";

class CaptionElement extends Element {
  static RESTRICTIONS = [
    // Basic actions
    "position",
    "visibility",
    "opacity",
    "angle",
    "duplicate",
    "removable",
    // Caption-specific
    "captionStyle",
    "captionColors",
    "captionEdit",
    "animations"
  ];

  /**
   * Get the active caption segment(s) for a given time
   * @param {number} currentTime - Current video playback time in seconds
   * @returns {Array} Active segments at the given time
   */
  getActiveSegments(currentTime) {
    return getActiveSegments(
      this.segments || [],
      currentTime,
      this.displaySettings?.showOneAtATime ?? true
    );
  }

  /**
   * Check if this caption is linked to a video element
   * @returns {boolean}
   */
  hasVideoLink() {
    return Boolean(this.videoElementId);
  }

  /**
   * Get the total duration of all caption segments
   * @returns {number} Duration in seconds
   */
  getTotalDuration() {
    if (!this.segments || this.segments.length === 0) {
      return 0;
    }

    const lastSegment = this.segments[this.segments.length - 1];
    return lastSegment ? lastSegment.endTime : 0;
  }

  /**
   * Check if caption has transcription data
   * @returns {boolean}
   */
  hasTranscription() {
    return Boolean(this.transcription && this.transcription.fullText);
  }

  /**
   * Get the number of caption segments
   * @returns {number}
   */
  getSegmentCount() {
    return this.segments ? this.segments.length : 0;
  }

  /**
   * Find a segment by ID
   * @param {string} segmentId - ID of the segment to find
   * @returns {Object|null} The segment or null if not found
   */
  getSegmentById(segmentId) {
    if (!this.segments) {
      return null;
    }
    return this.segments.find(seg => seg.id === segmentId) || null;
  }

  /**
   * Update attributes with validation for caption-specific properties
   * @param {Object} attributes - Attributes to update
   * @returns {CaptionElement} New instance with updated attributes
   */
  updateAttributes(attributes) {
    // Handle displaySettings updates by merging with existing settings
    if (attributes.displaySettings) {
      const currentDisplaySettings = this.displaySettings || {};
      attributes = {
        ...attributes,
        displaySettings: {
          ...currentDisplaySettings,
          ...attributes.displaySettings
        }
      };
    }

    // Handle highlightSettings updates by merging with existing settings
    if (attributes.highlightSettings) {
      const currentHighlightSettings = this.highlightSettings || {};
      attributes = {
        ...attributes,
        highlightSettings: {
          ...currentHighlightSettings,
          ...attributes.highlightSettings
        }
      };
    }

    // Handle style-related attributes that should apply to caption segments
    const styleAttributes = this._extractStyleAttributes(attributes);

    if (Object.keys(styleAttributes).length > 0) {
      // Update globalStyle with the new style attributes
      const currentGlobalStyle = this.globalStyle || {};
      const updatedGlobalStyle = {
        ...currentGlobalStyle,
        ...styleAttributes
      };

      // Scale bounding box proportionally when fontSize changes manually
      // (i.e., when fontSize changes but width/height are NOT explicitly set)
      // This keeps the caption visually consistent, similar to textbox behavior
      // Skip this when resize handles are used (width/height already set by resize)
      const isFontSizeChanging =
        styleAttributes.fontSize !== undefined &&
        currentGlobalStyle.fontSize &&
        currentGlobalStyle.fontSize > 0 &&
        styleAttributes.fontSize !== currentGlobalStyle.fontSize;

      const isManualFontSizeChange =
        isFontSizeChanging &&
        attributes.width === undefined &&
        attributes.height === undefined;

      if (isManualFontSizeChange) {
        const scale = styleAttributes.fontSize / currentGlobalStyle.fontSize;

        // Scale dimensions
        attributes.width = this.width * scale;
        attributes.height = this.height * scale;

        // Scale backgroundPadding proportionally
        if (currentGlobalStyle.backgroundPadding) {
          updatedGlobalStyle.backgroundPadding =
            currentGlobalStyle.backgroundPadding * scale;
          styleAttributes.backgroundPadding =
            updatedGlobalStyle.backgroundPadding;
        }

        // Scale borderRadius proportionally
        if (currentGlobalStyle.borderRadius) {
          updatedGlobalStyle.borderRadius =
            currentGlobalStyle.borderRadius * scale;
          styleAttributes.borderRadius = updatedGlobalStyle.borderRadius;
        }
      }

      // Keep style attributes at top level for compatibility with action bar
      // while also storing in globalStyle for caption rendering
      attributes = {
        ...attributes,
        ...styleAttributes,
        globalStyle: updatedGlobalStyle
      };

      // Also update all segment styles to maintain consistency
      if (this.segments && this.segments.length > 0) {
        attributes.segments = this.segments.map(segment => ({
          ...segment,
          style: {
            ...(segment.style || {}),
            ...styleAttributes
          }
        }));
      }
    }

    // Validate segments if they're being updated
    if (attributes.segments) {
      attributes.segments = this._validateSegments(attributes.segments);
    }

    return super.updateAttributes(attributes);
  }

  /**
   * Extract style-related attributes from general attributes
   * @private
   * @param {Object} attributes - All attributes
   * @returns {Object} Style attributes only
   */
  _extractStyleAttributes(attributes) {
    const styleKeys = [
      "fontFamily",
      "fontSize",
      "fontWeight",
      "fontStyle",
      "color",
      "textAlign",
      "backgroundColor",
      "backgroundOpacity",
      "backgroundPadding",
      "borderRadius"
    ];

    const styleAttrs = {};
    styleKeys.forEach(key => {
      if (attributes[key] !== undefined) {
        styleAttrs[key] = attributes[key];
      }
    });

    return styleAttrs;
  }

  /**
   * Validate caption segments
   * @private
   * @param {Array} segments - Segments to validate
   * @returns {Array} Validated segments
   */
  _validateSegments(segments) {
    if (!Array.isArray(segments)) {
      return [];
    }

    // Ensure segments are properly ordered by start time
    return segments
      .filter(seg => seg.startTime !== undefined && seg.endTime !== undefined)
      .sort((a, b) => a.startTime - b.startTime);
  }
}

export default CaptionElement;
